/**
 * Utilitário de gerenciamento de cargos e hierarquia (Frontend)
 * Fonte única de verdade para validações de permissões na UI
 * 
 * IMPORTANTE: Mantém sincronizado com backend/utils/cargos.js
 */

// Hierarquia de cargos (do maior para o menor poder)
export const HIERARQUIA_CARGOS = ['admin', 'sindico', 'portaria'];

// Map de índices para comparação rápida
const INDICES_CARGOS = HIERARQUIA_CARGOS.reduce((acc, cargo, index) => {
  acc[cargo] = index;
  return acc;
}, {});

/**
 * Verifica se um cargo é válido no sistema
 */
export function cargoValido(cargo) {
  return HIERARQUIA_CARGOS.includes(cargo);
}

/**
 * Verifica se o solicitante pode criar/gerenciar um cargo alvo
 * Regra: só pode criar/editar cargos de nível igual ou inferior
 * 
 * @param {string} cargoSolicitante - Cargo de quem está fazendo a operação
 * @param {string} cargoAlvo - Cargo que se deseja criar/editar
 * @returns {boolean} true se permitido
 */
export function podeGerenciar(cargoSolicitante, cargoAlvo) {
  if (!cargoValido(cargoSolicitante) || !cargoValido(cargoAlvo)) {
    return false;
  }
  
  // Admin pode gerenciar todos
  if (cargoSolicitante === 'admin') {
    return true;
  }
  
  // Outros cargos: só podem gerenciar abaixo deles
  return INDICES_CARGOS[cargoSolicitante] < INDICES_CARGOS[cargoAlvo];
}

/**
 * Retorna lista de cargos que um usuário pode criar
 * 
 * @param {string} cargo - Cargo do usuário logado
 * @returns {string[]} Array de cargos permitidos
 */
export function cargosPermitidos(cargo) {
  if (!cargoValido(cargo)) {
    return [];
  }
  
  // Admin pode criar todos os cargos
  if (cargo === 'admin') {
    return [...HIERARQUIA_CARGOS];
  }
  
  // Síndico só pode criar portaria
  if (cargo === 'sindico') {
    return ['portaria'];
  }
  
  // Portaria não pode criar ninguém
  return [];
}

/**
 * Verifica se um cargo é superior a outro
 * 
 * @param {string} cargo1 - Primeiro cargo
 * @param {string} cargo2 - Segundo cargo
 * @returns {boolean} true se cargo1 > cargo2
 */
export function cargoSuperior(cargo1, cargo2) {
  if (!cargoValido(cargo1) || !cargoValido(cargo2)) {
    return false;
  }
  return INDICES_CARGOS[cargo1] < INDICES_CARGOS[cargo2];
}

/**
 * Retorna o nome amigável do cargo para exibição
 */
export function nomeAmigavel(cargo) {
  const nomes = {
    admin: 'Administrador',
    sindico: 'Síndico',
    portaria: 'Portaria'
  };
  return nomes[cargo] || cargo;
}

/**
 * Retorna opções de cargo formatadas para dropdown
 * @param {string} cargoUsuarioLogado - Cargo do usuário logado
 * @returns {Array<{value: string, label: string}>}
 */
export function opcoesCargoParaSelect(cargoUsuarioLogado) {
  const permitidos = cargosPermitidos(cargoUsuarioLogado);
  return permitidos.map(cargo => ({
    value: cargo,
    label: nomeAmigavel(cargo)
  }));
}
